import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Which machines get a timeline row in the `/map` stack.
 *
 * The stack is the tallest thing on the page and every row costs real work — its
 * own events query, truck-events query and video-availability strip. So it starts
 * MINIMAL: the active machine only, with the rest opted in deliberately. A hidden
 * row is not collapsed, it is not rendered at all, so its provider unmounts and
 * its requests stop.
 *
 * Stores the SHOWN ids, which is what makes "hidden by default" hold for machines
 * this store has never seen — a new device, another location, a machine added
 * later. (Storing hidden ids would do the opposite: anything unknown appears, and
 * an eight-machine site greets you with eight rows again.) The active machine is
 * always rendered regardless, so an empty set still shows exactly one row.
 */
interface MapTimelineVisibilityState {
  shownDeviceMxids: string[];
  isShown: (deviceMxid: string) => boolean;
  toggleShown: (deviceMxid: string) => void;
  showAll: (deviceMxids: string[]) => void;
  hideAll: () => void;
}

export const useMapTimelineVisibilityStore = create<MapTimelineVisibilityState>()(
  persist(
    (set, get) => ({
      shownDeviceMxids: [],

      isShown: (deviceMxid) => get().shownDeviceMxids.includes(deviceMxid),

      toggleShown: (deviceMxid) =>
        set((state) => ({
          shownDeviceMxids: state.shownDeviceMxids.includes(deviceMxid)
            ? state.shownDeviceMxids.filter((id) => id !== deviceMxid)
            : [...state.shownDeviceMxids, deviceMxid],
        })),

      showAll: (deviceMxids) => set({ shownDeviceMxids: [...new Set(deviceMxids)] }),

      hideAll: () => set({ shownDeviceMxids: [] }),
    }),
    {
      // v2: the previous version stored HIDDEN ids, so carrying it over would
      // read every id as "shown" and invert the new default on first load.
      name: "map-timeline-visibility-v2",
    },
  ),
);

/**
 * The rows to render, in the given order.
 *
 * The ACTIVE machine is always kept: it owns the cursor the whole stack is
 * aligned to and the video that drives it, so hiding it would leave every other
 * row following a playhead nobody can see. The menu disables its entry for the
 * same reason; this is the backstop for a persisted state from when a different
 * machine was active.
 */
export const visibleTimelineMachines = <T extends { device_mxid: string }>(
  machines: T[],
  shownDeviceMxids: string[],
  activeDeviceMxid: string | null,
): T[] => {
  const shown = new Set(shownDeviceMxids);
  return machines.filter((m) => m.device_mxid === activeDeviceMxid || shown.has(m.device_mxid));
};
